import isEqual from 'lodash/isEqual';
import { convertPxToVw } from 'utils/helpers';
import type { StoryVersionType } from 'types/story';
import type { DesignDefinition, DesignModeSelection, DesignModeValue, DesignToken, DesignTokenSet } from './types';
import {
	assetIdToCssVariable,
	assetIdToReference,
	dimensionTokenIdToCssVariable,
	tokenIdToCssVariable,
} from './operations';
import { getDefaultDesignModeSelection, resolveDesignModeValue } from './modes';
import { isDesignTokenTypeCompatible, DESIGN_TOKEN_PROPERTY_CONFIG } from './properties';
import { collectStoryStyleAssignments } from './story-style-assignments';

export type {
	DesignPreset,
	DesignPresetProperty,
	DesignPresetPropertyValue,
	DesignPresetValue,
	DesignAsset,
	DesignAssetRole,
	DesignAssetSet,
	DesignDefinition,
	DesignMode,
	DesignModeActivation,
	DesignModeGroup,
	DesignModeSelection,
	DesignModeValue,
	DesignToken,
	DesignTokenSet,
	StoryDesignModeSettings,
	TokenCollection,
	DesignTokenType,
} from './types';
export * from './operations';
export * from './presets';
export * from './preset-styles';
export * from './preset-operations';
export * from './preset-fonts';
export * from './story-style-assignments';
export { DESIGN_PRESET_PROPERTIES } from './types';
export * from './values';
export * from './properties';
export * from './modes';

const TOKEN_VARIABLE_REGEXP = /var\(\s*(--sc-token-[a-z0-9-]+)(?:\s*,[^)]*)?\)/gi;
const EXACT_TOKEN_VARIABLE_REGEXP = /^var\(\s*(--sc-token-[a-z0-9-]+)(?:\s*,[^)]*)?\)$/i;
export type CompiledDesignToken = DesignToken & {
	collectionId: string;
	collectionName: string;
	cssVariable: string;
	reference: string;
};
export function flattenDesignTokens(tokens?: DesignTokenSet): CompiledDesignToken[] {
	if (!tokens) return [];
	return tokens.collections.flatMap(collection =>
		collection.tokens.map(token => ({
			...token,
			collectionId: collection.id,
			collectionName: collection.name,
			cssVariable: tokenIdToCssVariable(token.id),
			reference: `var(${tokenIdToCssVariable(token.id)})`,
		}))
	);
}
export function compileForAI(tokens?: DesignTokenSet) {
	return flattenDesignTokens(tokens).map(({ collectionName, id, name, type, value, reference, description }) => ({
		collectionName,
		id,
		name,
		type,
		value,
		reference,
		...(description ? { description } : {}),
	}));
}
function createTokenDeclarations(
	tokens: CompiledDesignToken[],
	selection: DesignModeSelection,
	dimensionPlatforms: Record<string, number>
): string {
	return tokens
		.flatMap(token => {
			const value = resolveDesignModeValue(token.value as DesignModeValue<string | number>, selection);
			const declarations = [`\t${token.cssVariable}: ${value};`];
			if (token.type === 'dimension' && /^[-+]?(?:\d+(?:\.\d+)?|\.\d+)px$/i.test(String(value))) {
				for (const [platform, referenceWidth] of Object.entries(dimensionPlatforms))
					declarations.push(
						`\t${dimensionTokenIdToCssVariable(token.id, platform)}: ${convertPxToVw(value, 2, referenceWidth)};`
					);
			}
			return declarations;
		})
		.join('\n');
}
export function createDesignTokenCss(
	design?: DesignDefinition,
	selector = ':root',
	dimensionPlatforms: Record<string, number> = {}
): string {
	if (!design) return `${selector} {\n\n}`;
	const compiled = flattenDesignTokens(design.tokens).filter(token => /^[a-z][a-z0-9-]*$/.test(token.id));
	return createModeAwareCss(design, selector, compiled, (token, selection) =>
		createTokenDeclarations([token], selection, dimensionPlatforms).split('\n')
	);
}

/**
 * Token and asset variables for one fixed mode selection, for editor surfaces outside the Story (Inspector
 * previews) that show values with the same `var()` references the Story stores.
 */
export function createDesignVariableDeclarations(
	design: DesignDefinition | undefined,
	selection: DesignModeSelection,
	toCssImage: (src: string) => string
): string {
	if (!design) return '';
	const tokens = flattenDesignTokens(design.tokens).filter(token => /^[a-z][a-z0-9-]*$/.test(token.id));
	const assets = design.assets.assets.map(
		asset => `\t${assetIdToCssVariable(asset.id)}: ${toCssImage(resolveDesignModeValue(asset.value, selection))};`
	);
	return [createTokenDeclarations(tokens, selection, {}), ...assets].filter(Boolean).join('\n');
}

/**
 * Semantic image assets as `--sc-asset-<id>: url('…')`, resolved per mode exactly like tokens, so layered
 * backgrounds reference them with `var()` and a building block's local mode override switches its own image.
 */
export function createDesignAssetCss(
	design: DesignDefinition | undefined,
	selector: string,
	toCssImage: (src: string) => string
): string {
	const assets = (design?.assets.assets ?? []).filter(asset => /^[a-z][a-z0-9-]*$/.test(asset.id));
	if (!design || !assets.length) return '';
	return createModeAwareCss(design, selector, assets, (asset, selection) => [
		`\t${assetIdToCssVariable(asset.id)}: ${toCssImage(resolveDesignModeValue(asset.value, selection))};`,
	]);
}

function createModeAwareCss<T extends { value: DesignModeValue<unknown> }>(
	design: DesignDefinition,
	selector: string,
	items: T[],
	declare: (item: T, selection: DesignModeSelection) => string[]
): string {
	const baseSelection = getDefaultDesignModeSelection(design.modeGroups);
	const rules = [`${selector} {\n${items.flatMap(item => declare(item, baseSelection)).join('\n')}\n}`];
	const declarations = new Map<string, string[]>();
	const addDeclaration = (selection: DesignModeSelection, item: T) => {
		const modeSelector = Object.entries(selection)
			.map(([groupId, modeId]) => `[data-sc-mode-${groupId}="${modeId}"]`)
			.join('');
		const entries = declarations.get(modeSelector) ?? [];
		entries.push(...declare(item, { ...baseSelection, ...selection }));
		declarations.set(modeSelector, entries);
	};

	for (const item of items) {
		const affectedGroups = new Set((item.value.overrides ?? []).flatMap(override => Object.keys(override.when)));
		for (const groupId of affectedGroups) {
			const group = design.modeGroups.find(entry => entry.id === groupId)!;
			// Emit every mode, including the default value. A nested Light scope inside a Dark card must actively
			// reset the inherited custom property instead of relying on the root declaration.
			for (const mode of group.modes) addDeclaration({ [groupId]: mode.id }, item);
		}
		for (const override of item.value.overrides ?? []) {
			if (Object.keys(override.when).length > 1) addDeclaration(override.when, item);
		}
	}
	for (const [modeSelector, entries] of [...declarations].sort(
		([left], [right]) => left.split('][').length - right.split('][').length
	))
		rules.push(`${modeSelector} {\n${entries.join('\n')}\n}`);

	return rules.join('\n');
}
export function getCompatibleDesignTokens(tokens: DesignTokenSet | undefined, property: string): CompiledDesignToken[] {
	return flattenDesignTokens(tokens).filter(token => isDesignTokenTypeCompatible(property, token.type));
}
export function getChangedUsedFontTokenFamilies({
	data,
	before,
	after,
}: {
	data: StoryVersionType['data'];
	before?: DesignDefinition;
	after?: DesignDefinition;
}): string[] {
	const assignments = collectStoryStyleAssignments({ data, design: after }).map(item => item.assignments.styles);
	const used = new Set(reviewDesignTokenUsage(assignments, after?.tokens).referencedTokens);
	const previous = new Map(flattenDesignTokens(before?.tokens).map(token => [token.cssVariable, token.value]));
	return [
		...new Set(
			flattenDesignTokens(after?.tokens)
				.filter(
					token =>
						token.type === 'fontFamily' &&
						used.has(token.id) &&
						!isEqual(previous.get(token.cssVariable), token.value)
				)
				.flatMap(token => [token.value.base, ...(token.value.overrides ?? []).map(item => item.value)])
				.map(String)
		),
	];
}

const ASSET_REFERENCE_REGEXP = /^var\(\s*--sc-asset-([a-z][a-z0-9-]*)\s*\)$/i;
const EMBEDDED_ASSET_REFERENCE_REGEXP = /var\(\s*--sc-asset-([a-z][a-z0-9-]*)\s*\)/gi;
// The retired `asset(<id>)` form renders as a broken url(); review reports it instead of resolving it.
const RETIRED_ASSET_REFERENCE_REGEXP = /(?:^|[\s,(])asset\(/i;

/** The asset id of an exact `var(--sc-asset-<id>)` reference, for consumers that need a URL rather than CSS. */
export function getDesignAssetReferenceId(reference: string): string | undefined {
	return reference.trim().match(ASSET_REFERENCE_REGEXP)?.[1];
}

/** Asset ids referenced anywhere in a value, such as one layer of a layered background. */
export function getEmbeddedDesignAssetIds(value: string): string[] {
	return [...value.matchAll(EMBEDDED_ASSET_REFERENCE_REGEXP)].map(match => match[1]!);
}

export function resolveDesignAssetReference(
	reference: string,
	design: DesignDefinition | undefined,
	selection: DesignModeSelection
): string | undefined {
	const id = getDesignAssetReferenceId(reference);
	if (!id) return undefined;
	const asset = design?.assets.assets.find(item => item.id === id);
	return asset ? resolveDesignModeValue(asset.value, selection) : undefined;
}

/** Replaces each `var(--sc-asset-<id>)` with the asset's `url()` for surfaces without the Story's asset CSS. */
export function resolveDesignAssetVariables(
	value: string,
	design: DesignDefinition | undefined,
	selection: DesignModeSelection
): string {
	return value.replace(EMBEDDED_ASSET_REFERENCE_REGEXP, (reference, id: string) => {
		const asset = design?.assets.assets.find(item => item.id === id);
		return asset ? `url('${resolveDesignModeValue(asset.value, selection)}')` : reference;
	});
}

export function compileDesignAssetsForAI(design?: DesignDefinition) {
	return (design?.assets.assets ?? []).map(asset => ({
		id: asset.id,
		name: asset.name,
		role: asset.role,
		type: asset.type,
		value: asset.value,
		reference: assetIdToReference(asset.id),
		...(asset.description ? { description: asset.description } : {}),
	}));
}

export function reviewDesignAssetUsage(value: unknown, design?: DesignDefinition) {
	const knownIds = new Set(design?.assets.assets.map(asset => asset.id) ?? []);
	const referencedAssets = new Set<string>();
	const unknownReferences = new Set<string>();
	const retiredReferences = new Set<string>();
	const visit = (current: unknown): void => {
		if (typeof current === 'string') {
			for (const id of getEmbeddedDesignAssetIds(current))
				(knownIds.has(id) ? referencedAssets : unknownReferences).add(id);
			if (RETIRED_ASSET_REFERENCE_REGEXP.test(current)) retiredReferences.add(current);
			return;
		}
		if (Array.isArray(current)) current.forEach(visit);
		else if (current && typeof current === 'object') Object.values(current).forEach(visit);
	};
	visit(value);
	return {
		referencedAssets: [...referencedAssets].sort(),
		unknownReferences: [...unknownReferences].sort(),
		retiredReferences: [...retiredReferences].sort(),
	};
}

export function resolveDesignTokenReference(
	reference: string,
	tokens?: DesignTokenSet
): CompiledDesignToken | undefined {
	const cssVariable = reference.trim().match(EXACT_TOKEN_VARIABLE_REGEXP)?.[1];
	if (!cssVariable) return undefined;
	return flattenDesignTokens(tokens).find(token => token.cssVariable === cssVariable);
}

export function reviewDesignTokenUsage(value: unknown, designTokens?: DesignTokenSet) {
	const tokens = flattenDesignTokens(designTokens);
	const tokenPathByVariable = new Map(tokens.map(token => [token.cssVariable, token.id]));
	const incompatibleReferences = new Set<string>();
	const referencedTokens = new Set<string>();
	const unknownReferences = new Set<string>();
	let referenceCount = 0;

	const visit = (current: unknown, property?: string): void => {
		if (typeof current === 'string') {
			TOKEN_VARIABLE_REGEXP.lastIndex = 0;
			for (const match of current.matchAll(TOKEN_VARIABLE_REGEXP)) {
				const cssVariable = match[1]!;
				referenceCount += 1;
				const path = tokenPathByVariable.get(cssVariable);
				if (path) {
					referencedTokens.add(path);
					const token = tokens.find(t => t.id === path)!;
					if (property && !isDesignTokenTypeCompatible(property, token.type))
						incompatibleReferences.add(`${property}: ${cssVariable}`);
				} else unknownReferences.add(cssVariable);
			}
			return;
		}

		if (Array.isArray(current)) {
			current.forEach(item => visit(item, property));
			return;
		}

		if (current && typeof current === 'object')
			Object.entries(current).forEach(([key, item]) =>
				visit(item, DESIGN_TOKEN_PROPERTY_CONFIG[key] ? key : property)
			);
	};

	visit(value);

	return {
		referenceCount,
		incompatibleReferences: [...incompatibleReferences].sort(),
		referencedTokens: [...referencedTokens].sort(),
		unknownReferences: [...unknownReferences].sort(),
	};
}
